import { Head, Link, useForm, usePage } from '@inertiajs/react'
import { FormEvent, useState } from 'react'
import Alert from '../../components/Alert'
import CustomButtons, { CustomButton } from '../../components/CustomButtons'
import PageHeader, { Wave } from '../../components/PageHeader'
import PhoneField from '../../components/PhoneField'
import TextField from '../../components/TextField'
import { Flash } from '../../types'

// One form, three entry points (RegistrationsController): general, invite, prospect.
type Props = {
  variant: 'general' | 'invite' | 'prospect'
  titles: string[]
  pdpNotice: CustomButton[]
  referrer?: { name: string }
  linkUsed?: boolean
}

type YesNo = '' | 'yes' | 'no'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default function RegisterForm({ variant, titles, pdpNotice, referrer, linkUsed }: Props) {
  const { props, url } = usePage<{ flash: Flash }>()
  const form = useForm({
    title: '',
    name: '',
    email: '',
    nric: '',
    phone: '',
    ticket_number: '',
    member: '' as YesNo,
    referred: '' as YesNo,
    referrer_name: '',
    referrer_phone: '',
    pdpa_consent: false,
  })
  const { data, setData, errors } = form
  const [emailError, setEmailError] = useState('')
  const general = variant === 'general'
  const askReferred = general && data.member === 'no'
  const showReferrer = askReferred && data.referred === 'yes'

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.post(url.split('?')[0])
  }

  if (linkUsed) {
    return (
      <>
        <Head title="Invite already used" />
        <main className="ho-screen">
          <PageHeader title="This invite link has already been used" />
          <div className="ho-form">
            <p>Each invite link works for one person only. Ask the friend who invited you for help, or register on your own.</p>
            <Link className="ho-btn ho-btn-primary" href="/register">Register for the event</Link>
          </div>
          <Wave />
        </main>
      </>
    )
  }

  const yesNo = (field: 'member' | 'referred', legend: string) => (
    <fieldset className="ho-field" aria-invalid={!!errors[field]}>
      <legend className="ho-label mb-3">
        {legend}
        <span className="ho-req" aria-hidden="true">*</span>
      </legend>
      <div className="ho-opts">
        {(['yes', 'no'] as const).map((v) => (
          <label key={v} className="ho-opt">
            <input type="radio" name={field} value={v} required checked={data[field] === v} onChange={() => setData(field, v)} />
            {v === 'yes' ? 'Yes' : 'No'}
          </label>
        ))}
      </div>
      {errors[field] && <p className="ho-error">{errors[field]}</p>}
    </fieldset>
  )

  return (
    <>
      <Head title="Event Registration" />
      <main className="ho-screen">
        <PageHeader title="Event Registration" subtitle="Complete your details to receive your event pass." />
        <form className="ho-form" onSubmit={submit}>
          {props.flash?.alert && <Alert tone="error">{props.flash.alert}</Alert>}
          {referrer && <Alert tone="info">Referred by <strong>{referrer.name}</strong></Alert>}

          <div className="flex items-start gap-4">
            <div className="ho-field" style={{ flex: '0 0 136px' }}>
              <label className="ho-label" htmlFor="title">
                Title<span className="ho-req" aria-hidden="true">*</span>
              </label>
              <div className="ho-control" aria-invalid={!!errors.title}>
                <select id="title" name="title" required value={data.title} onChange={(e) => setData('title', e.target.value)}>
                  <option value="" disabled>Select</option>
                  {titles.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              {errors.title && <p className="ho-error">{errors.title}</p>}
            </div>
            <TextField
              className="flex-1"
              label="Full Name"
              name="name"
              required
              autoComplete="name"
              placeholder="As shown on your ID"
              value={data.name}
              onChange={(e) => setData('name', e.target.value)}
              error={errors.name}
            />
          </div>

          <TextField
            label="Email Address"
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder="name@email.com"
            value={data.email}
            onChange={(e) => {
              setData('email', e.target.value)
              setEmailError('')
            }}
            onBlur={(e) => {
              const v = e.target.value.trim()
              setEmailError(v && !EMAIL_RE.test(v) ? 'Enter a valid email address, e.g. name@email.com' : '')
            }}
            error={emailError || errors.email}
          />
          <TextField
            label="NRIC Number / Passport"
            name="nric"
            required
            placeholder="XXXXXXXXXXXX / AXXXXXXXX"
            autoCapitalize="characters"
            value={data.nric}
            onChange={(e) => setData('nric', e.target.value.replace(/[^a-zA-Z0-9]/g, ''))}
            error={errors.nric}
          />
          <PhoneField label="Phone Number" name="phone" required onChange={(v) => setData('phone', v)} error={errors.phone} />
          <TextField
            label="Ticket Number"
            name="ticket_number"
            required
            placeholder="Enter number on your ticket"
            value={data.ticket_number}
            onChange={(e) => setData('ticket_number', e.target.value.replace(/[^a-zA-Z0-9]/g, ''))}
            error={errors.ticket_number}
          />

          {general && yesNo('member', 'Are you a SHOM Registered Distributor?')}
          {askReferred && yesNo('referred', 'Were you referred by a SHOM registered distributor?')}
          {showReferrer && (
            <div className="ho-ref-box">
              <TextField
                label="Referrer’s Name"
                name="referrer_name"
                required
                placeholder="Member’s full name"
                value={data.referrer_name}
                onChange={(e) => setData('referrer_name', e.target.value)}
                error={errors.referrer_name}
              />
              <PhoneField
                label="Referrer’s Phone Number"
                name="referrer_phone"
                required
                onChange={(v) => setData('referrer_phone', v)}
                error={errors.referrer_phone}
              />
              <p className="ho-hint" style={{ marginTop: -8 }}>We’ll use this to match you with the SHOM registered distributor who invited you.</p>
            </div>
          )}

          <div>
            <label className="ho-check">
              <input
                type="checkbox"
                name="pdpa_consent"
                required
                checked={data.pdpa_consent}
                onChange={(e) => setData('pdpa_consent', e.target.checked)}
              />
              <span>
                I hereby authorize Sahajidah Hai-O Marketing Sdn Bhd (SHOM) to collect, use, and process my personal data
                (including name, email, mobile number) for registration, event management, follow-up, feedback collection
                related to this event in compliance with the Personal Data Protection Act (PDPA) 2010. I have read and fully
                understand the{' '}
                {pdpNotice.length ? <CustomButtons buttons={pdpNotice} /> : 'Personal Data Privacy Notice (PDP Notice)'} as
                attached pursuant to PDPA 2010 and consent to the processing of my personal data in accordance with the PDP Notice.
              </span>
            </label>
            {errors.pdpa_consent && <p className="ho-error mt-2">{errors.pdpa_consent}</p>}
          </div>

          <button type="submit" className="ho-btn ho-btn-primary" disabled={form.processing}>
            {form.processing ? 'Creating your pass…' : 'Create my event pass'}
          </button>
          <p className="ho-foot">Already registered? <Link href="/login">Log in</Link></p>
        </form>
        <Wave />
      </main>
    </>
  )
}
